import { parseXmlDocument, serializeXmlDocument, type DomParserLike } from '../services/xmlParser';
import type { License, ProductLicense } from '../types/license-types';
import type { ProjectLicenseMetadata } from '../types/project-types';

export type LicenseCompatibilityStatus = 'allowed' | 'disallowed' | 'requires-assignment' | 'git-remote-mismatch';

export type LicenseCompatibility = {
    status: LicenseCompatibilityStatus;
    message: string;
    licenseMetadata?: ProjectLicenseMetadata;
};

export type LicenseCompatibilityOptions = {
    workspaceGitRemoteUrls?: readonly string[];
    workspaceIsGitRepository?: boolean;
    gitInstalled?: boolean;
};

export const GIT_NOT_INSTALLED_MESSAGE: string =
    'This license requires git to be installed, but it was not found. Please install it and try again.';

export const DUMMY_GIT_REMOTE_URL: string = 'dummy';

export const formatGitRemoteMismatchMessage = (expectedGitUrl: string, isGitRepository: boolean): string => {
    const gitUrl: string = expectedGitUrl.trim();
    if (!isGitRepository) {
        return `No git remote was found with ${gitUrl}. Please run \`git init\` and \`git remote add origin ${gitUrl}\`.`;
    }
    return `No git remote was found with ${gitUrl}. Please run \`git remote add origin ${gitUrl}\`.`;
};

/** License metadata git_url values that are not real repository URLs (e.g. test placeholders). */
export const isPlaceholderGitUrl = (gitUrl: string): boolean => {
    const trimmedUrl: string = gitUrl.trim();
    if (!trimmedUrl) return false;
    if (trimmedUrl.includes('://')) return false;
    if (/^(?:[^@/:]+@)?[^:/]+:.+/.test(trimmedUrl)) return false;
    return true;
};

export const resolveProductLicenseGitRemoteUrl = (gitUrl: string): string =>
    isPlaceholderGitUrl(gitUrl) ? DUMMY_GIT_REMOTE_URL : gitUrl;

const getLicenseMetadata = (license: ProductLicense): ProjectLicenseMetadata => ({
    product_id: license.product_id,
    git_url: license.git_url
});

const getAttribute = (value: string | null | undefined): string | undefined =>
    value === undefined || value === null ? undefined : value;

const hasMetadataValue = (value: string | undefined): value is string => value !== undefined && value.trim().length > 0;

const hasCompleteLicenseMetadata = (
    metadata: ProjectLicenseMetadata | null
): metadata is Required<ProjectLicenseMetadata> =>
    hasMetadataValue(metadata?.product_id) && hasMetadataValue(metadata?.git_url);

const hasAnyLicenseMetadata = (metadata: ProjectLicenseMetadata | null): boolean =>
    hasMetadataValue(metadata?.product_id) || hasMetadataValue(metadata?.git_url);

const normalizeGitHostAndPath = (host: string, repoPath: string): string => {
    const normalizedPath: string = repoPath
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')
        .replace(/\.git$/i, '');
    return `${host.toLowerCase()}/${normalizedPath.toLowerCase()}`;
};

const normalizeGitUrlForComparison = (gitUrl: string): string => {
    const trimmedUrl: string = gitUrl.trim().replace(/\s+\((fetch|push)\)$/i, '');
    if (!trimmedUrl) return '';

    if (!trimmedUrl.includes('://')) {
        const scpLikeMatch: RegExpMatchArray | null = trimmedUrl.match(/^(?:[^@/:]+@)?([^:]+):(.+)$/);
        if (scpLikeMatch) return normalizeGitHostAndPath(scpLikeMatch[1], scpLikeMatch[2]);
    }

    try {
        const parsedUrl: URL = new URL(trimmedUrl);
        return normalizeGitHostAndPath(
            parsedUrl.port ? `${parsedUrl.hostname}:${parsedUrl.port}` : parsedUrl.hostname,
            parsedUrl.pathname
        );
    } catch {
        return trimmedUrl
            .replace(/\/+$/, '')
            .replace(/\.git$/i, '')
            .toLowerCase();
    }
};

export const isProductLicense = (license: License): license is ProductLicense => license.licenseTier === 'Product';

export const parseGitRemoteOutput = (gitRemoteOutput: string): string[] => {
    const remoteUrls: string[] = [];
    const normalizedRemoteUrls: Set<string> = new Set<string>();

    for (const line of gitRemoteOutput.split(/\r?\n/)) {
        const parts: string[] = line.trim().split(/\s+/);
        if (parts.length < 2) continue;

        const remoteUrl: string = parts[1];
        const normalizedRemoteUrl: string = normalizeGitUrlForComparison(remoteUrl);
        if (!normalizedRemoteUrl || normalizedRemoteUrls.has(normalizedRemoteUrl)) continue;

        normalizedRemoteUrls.add(normalizedRemoteUrl);
        remoteUrls.push(remoteUrl);
    }

    return remoteUrls;
};

export const hasMatchingGitRemote = (workspaceGitRemoteUrls: readonly string[], expectedGitUrl: string): boolean => {
    const normalizedExpectedGitUrl: string = normalizeGitUrlForComparison(
        resolveProductLicenseGitRemoteUrl(expectedGitUrl)
    );
    if (!normalizedExpectedGitUrl) return false;

    return workspaceGitRemoteUrls.some(
        (workspaceGitRemoteUrl: string): boolean =>
            normalizeGitUrlForComparison(workspaceGitRemoteUrl) === normalizedExpectedGitUrl
    );
};

const getProjectElement = (projectDocument: Document): Element | null => {
    const rootElement: Element | null = projectDocument.documentElement;
    return rootElement?.tagName === 'project' ? rootElement : projectDocument.querySelector('project');
};

const parseProjectXml = (projectXML: string, parser?: DomParserLike): Document =>
    parseXmlDocument(projectXML, 'Could not parse project XML.', undefined, parser);

export const getProjectLicenseCompatibility = (
    projectXML: string,
    selectedLicense: License,
    options: LicenseCompatibilityOptions = {}
): LicenseCompatibility => {
    const projectMetadata: ProjectLicenseMetadata | null = readProjectLicenseMetadata(projectXML);

    if (selectedLicense.licenseTier === 'Platform') {
        return { status: 'allowed', message: 'The selected license is compatible with the project.' };
    }

    if (!isProductLicense(selectedLicense)) {
        if (hasAnyLicenseMetadata(projectMetadata)) {
            return {
                status: 'disallowed',
                message:
                    'This project is assigned to a product license. Select a Product or Platform license to open this project.'
            };
        }

        return { status: 'allowed', message: 'The selected license is compatible with the project.' };
    }

    const selectedLicenseMetadata: ProjectLicenseMetadata = getLicenseMetadata(selectedLicense);
    const selectedProductName: string = selectedLicense.product_id || selectedLicense.displayName;

    if (!hasCompleteLicenseMetadata(selectedLicenseMetadata)) {
        return {
            status: 'disallowed',
            message: 'This Product license is missing product_id or git_url metadata and cannot open projects.'
        };
    }

    const projectProductIdIsSet: boolean = hasMetadataValue(projectMetadata?.product_id);
    const projectGitUrlIsSet: boolean = hasMetadataValue(projectMetadata?.git_url);
    const projectMetadataDiffers: boolean =
        (projectProductIdIsSet && projectMetadata?.product_id !== selectedLicenseMetadata.product_id) ||
        (projectGitUrlIsSet && projectMetadata?.git_url !== selectedLicenseMetadata.git_url);

    if (projectMetadataDiffers) {
        const projectProductId: string = projectProductIdIsSet ? `"${projectMetadata?.product_id}"` : 'not set';
        const projectGitUrl: string = projectGitUrlIsSet ? `"${projectMetadata?.git_url}"` : 'not set';
        return {
            status: 'disallowed',
            message: `This project is assigned to product_id ${projectProductId} and git_url ${projectGitUrl}, but the selected license is for product_id "${selectedLicenseMetadata.product_id}" and git_url "${selectedLicenseMetadata.git_url}". Select a matching license to open this project.`
        };
    }

    if (!hasCompleteLicenseMetadata(projectMetadata)) {
        return {
            status: 'requires-assignment',
            message: `This project is not assigned to a product license. Do you want to assign this project to ${selectedProductName}?`,
            licenseMetadata: selectedLicenseMetadata
        };
    }

    if (options.gitInstalled === false) {
        return {
            status: 'git-remote-mismatch',
            message: GIT_NOT_INSTALLED_MESSAGE
        };
    }

    if (
        options.workspaceGitRemoteUrls !== undefined &&
        !hasMatchingGitRemote(options.workspaceGitRemoteUrls, selectedLicenseMetadata.git_url)
    ) {
        const isGitRepository: boolean = options.workspaceIsGitRepository === true;
        return {
            status: 'git-remote-mismatch',
            message: formatGitRemoteMismatchMessage(selectedLicenseMetadata.git_url, isGitRepository)
        };
    }

    return { status: 'allowed', message: 'The selected license is compatible with the project.' };
};

export const readProjectLicenseMetadata = (projectXML: string): ProjectLicenseMetadata | null => {
    const projectDocument: Document = parseProjectXml(projectXML);
    const project: Element | null = getProjectElement(projectDocument);
    const metadata: ProjectLicenseMetadata = {
        product_id: getAttribute(project?.getAttribute('product_id')),
        git_url: getAttribute(project?.getAttribute('git_url'))
    };

    return metadata.product_id === undefined && metadata.git_url === undefined ? null : metadata;
};

export const writeProjectLicenseMetadata = (
    projectXML: string,
    licenseMetadata: ProjectLicenseMetadata,
    parser?: DomParserLike
): string => {
    const projectDocument: Document = parseProjectXml(projectXML, parser);
    const project: Element | null = getProjectElement(projectDocument);
    if (!project) return serializeXmlDocument(projectDocument);

    if (licenseMetadata.product_id !== undefined) project.setAttribute('product_id', licenseMetadata.product_id);

    if (licenseMetadata.git_url !== undefined) project.setAttribute('git_url', licenseMetadata.git_url);

    return serializeXmlDocument(projectDocument);
};
